import { useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { EducationEntry } from 'utils/cv-form-db/types';
import { usePaginatedQuery } from '../../hooks';
import {
  useDegreesInfiniteQuery,
  useStudiesInfiniteQuery,
  useUniversitiesInfiniteQuery
} from './query';
import {
  compareSemesters,
  generateSemesterOptions,
  isEducationComplete
} from './utils';

export const useUniversitiesNames = () => {
  const infiniteQuery = useUniversitiesInfiniteQuery();
  return usePaginatedQuery(infiniteQuery, 'Universities');
};

export const useStudiesNames = () => {
  const infiniteQuery = useStudiesInfiniteQuery();
  return usePaginatedQuery(infiniteQuery, 'Studies');
};

export const useDegreesNames = () => {
  const infiniteQuery = useDegreesInfiniteQuery();
  return usePaginatedQuery(infiniteQuery, 'Degrees');
};

export const useEducationForm = (values: EducationEntry) => {
  const { setFieldValue } = useFormikContext<EducationEntry>();
  const { options: semesterOptions, currentSemester } = useMemo(
    () => generateSemesterOptions(),
    []
  );

  useEffect(() => {
    if (!values.studyStart) {
      void setFieldValue('studyStart', currentSemester);
    }
  }, [values.studyStart, currentSemester, setFieldValue]);

  useEffect(() => {
    if (values.studyStart && values.studyEnd) {
      if (compareSemesters(values.studyStart, values.studyEnd) > 0) {
        void setFieldValue('studyEnd', '');
      }
    }
  }, [values.studyStart, values.studyEnd, setFieldValue]);

  const endDateOptions = useMemo(() => {
    if (!values.studyStart) return semesterOptions;
    return semesterOptions.filter(
      option => compareSemesters(values.studyStart || '', option.value) <= 0
    );
  }, [values.studyStart, semesterOptions]);

  return {
    semesterOptions,
    endDateOptions,
    isComplete: isEducationComplete(values)
  };
};
